import axios from 'axios';

// Crea istanza axios con configurazione base
const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Per i cookie del refresh token
});

// Interceptor per aggiungere il token alle richieste
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor per gestire errori e refresh token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Non fare refresh token per le richieste di login/auth
    const isAuthRequest = originalRequest.url?.includes('/auth/login') ||
                          originalRequest.url?.includes('/auth/verify-2fa') ||
                          originalRequest.url?.includes('/auth/forgot-password') ||
                          originalRequest.url?.includes('/auth/reset-password');

    // Se errore 401, non è già un retry, e NON è una richiesta di auth
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthRequest) {
      originalRequest._retry = true;

      try {
        // Prova a refreshare il token
        const response = await axios.post(
          '/api/auth/refresh-token',
          {},
          { withCredentials: true }
        );

        const { accessToken } = response.data.data;
        localStorage.setItem('accessToken', accessToken);

        // Riprova la richiesta originale
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh fallito, logout
        localStorage.removeItem('accessToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// ============================================
// SERVIZI AUTENTICAZIONE
// ============================================

export const authService = {
  /**
   * Login utente
   */
  login: async (email, password) => {
    // DEBUG: Log prima della richiesta
    const payload = { email, password };
    console.log('=== DEBUG API LOGIN ===');
    console.log('Payload email:', payload.email);
    console.log('JSON.stringify:', JSON.stringify(payload));

    const response = await api.post('/auth/login', payload);
    return response.data;
  },

  /**
   * Verifica codice 2FA
   */
  verify2FA: async (userId, code) => {
    const response = await api.post('/auth/verify-2fa', { userId, code });
    return response.data;
  },

  /**
   * Logout
   */
  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  /**
   * Ottieni profilo utente corrente
   */
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  /**
   * Aggiorna profilo utente
   */
  updateMe: async (data) => {
    const response = await api.put('/auth/me', data);
    return response.data;
  },

  /**
   * Cambia password
   */
  changePassword: async (currentPassword, newPassword, confirmPassword) => {
    const response = await api.put('/auth/change-password', {
      currentPassword,
      newPassword,
      confirmPassword,
    });
    return response.data;
  },

  /**
   * Richiedi reset password
   */
  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  /**
   * Reset password con token
   */
  resetPassword: async (token, password, confirmPassword) => {
    const response = await api.post(`/auth/reset-password/${token}`, {
      password,
      confirmPassword,
    });
    return response.data;
  },

  /**
   * Abilita 2FA
   */
  enable2FA: async () => {
    const response = await api.put('/auth/enable-2fa');
    return response.data;
  },

  /**
   * Disabilita 2FA
   */
  disable2FA: async (password) => {
    const response = await api.put('/auth/disable-2fa', { password });
    return response.data;
  },
};

// ============================================
// SERVIZI DOCUMENTI
// ============================================

export const documentService = {
  /**
   * Lista documenti
   */
  getAll: async (params = {}) => {
    const response = await api.get('/documents', { params });
    return response.data;
  },

  /**
   * Dettaglio documento
   */
  getById: async (id) => {
    const response = await api.get(`/documents/${id}`);
    return response.data;
  },

  /**
   * Documenti di un cliente
   */
  getByClient: async (clientId, params = {}) => {
    const response = await api.get(`/documents/client/${clientId}`, { params });
    return response.data;
  },

  /**
   * Upload documento
   */
  upload: async (formData) => {
    const response = await api.post('/documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Download documento
   */
  download: async (id) => {
    const response = await api.get(`/documents/${id}/download`, {
      responseType: 'blob',
    });
    return response;
  },

  /**
   * Elimina documento
   */
  delete: async (id) => {
    const response = await api.delete(`/documents/${id}`);
    return response.data;
  },

  /**
   * Statistiche documenti (admin)
   */
  getStats: async () => {
    const response = await api.get('/documents/stats');
    return response.data;
  },
};

// ============================================
// SERVIZI FIRMA DOCUMENTI
// ============================================

export const signatureService = {
  /**
   * Firma un documento (solo client)
   */
  sign: async (documentId, data = {}) => {
    const response = await api.post(`/signatures/${documentId}`, data);
    return response.data;
  },

  /**
   * Ottieni firme di un documento
   */
  getByDocument: async (documentId) => {
    const response = await api.get(`/signatures/${documentId}`);
    return response.data;
  },

  /**
   * Verifica integrità firma
   */
  verify: async (documentId, signatureId) => {
    const response = await api.get(`/signatures/${documentId}/verify`, {
      params: { signatureId },
    });
    return response.data;
  },

  /**
   * Documenti in attesa di firma (solo client)
   */
  getPending: async () => {
    const response = await api.get('/signatures/pending');
    return response.data;
  },

  /**
   * Stato firma di un documento
   */
  getStatus: async (documentId) => {
    const response = await api.get(`/signatures/document/${documentId}/status`);
    return response.data;
  },
};

// ============================================
// SERVIZI UTENTI
// ============================================

export const userService = {
  /**
   * Lista utenti (admin)
   */
  getAll: async (params = {}) => {
    const response = await api.get('/users', { params });
    return response.data;
  },

  /**
   * Dettaglio utente
   */
  getById: async (id) => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  /**
   * Crea utente (admin)
   */
  create: async (data) => {
    const response = await api.post('/users', data);
    return response.data;
  },

  /**
   * Aggiorna utente (admin)
   */
  update: async (id, data) => {
    const response = await api.put(`/users/${id}`, data);
    return response.data;
  },

  /**
   * Disattiva utente (admin)
   */
  delete: async (id) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },

  /**
   * Elimina definitivamente utente e documenti (admin)
   */
  permanentDelete: async (id) => {
    const response = await api.delete(`/users/${id}/permanent`);
    return response.data;
  },

  /**
   * Lista operatori
   */
  getOperators: async () => {
    const response = await api.get('/users/operators');
    return response.data;
  },

  /**
   * Lista clienti
   */
  getClients: async (params = {}) => {
    const response = await api.get('/users/clients', { params });
    return response.data;
  },

  /**
   * Assegna cliente a operatore
   */
  assignClient: async (clientId, operatorId) => {
    const response = await api.put(`/users/${clientId}/assign/${operatorId}`);
    return response.data;
  },

  /**
   * Rimuovi assegnazione cliente
   */
  unassignClient: async (clientId) => {
    const response = await api.delete(`/users/${clientId}/unassign`);
    return response.data;
  },
};

// ============================================
// SERVIZI OPERATORE
// ============================================

export const operatorService = {
  /**
   * Lista clienti assegnati
   */
  getMyClients: async () => {
    const response = await api.get('/operator/clients');
    return response.data;
  },

  /**
   * Dettaglio cliente assegnato
   */
  getMyClient: async (id) => {
    const response = await api.get(`/operator/clients/${id}`);
    return response.data;
  },
};

// ============================================
// SERVIZI GDPR
// ============================================

export const gdprService = {
  /**
   * Export dati personali (GDPR Art. 20)
   */
  exportData: async () => {
    const response = await api.get('/gdpr/export');
    return response.data;
  },

  /**
   * Cancellazione account (GDPR Art. 17)
   */
  deleteAccount: async (password) => {
    const response = await api.delete('/gdpr/delete-account', {
      data: { password },
    });
    return response.data;
  },

  /**
   * Accetta informativa privacy
   */
  acceptPrivacy: async () => {
    const response = await api.put('/gdpr/accept-privacy');
    return response.data;
  },
};
